import { Link } from "react-router-dom";
import { IMAGES, IMAGE_CAPTIONS, AWARDS } from "../lib/content";
import ZoomableImage from "../components/site/ZoomableImage";

const TIMELINE = [
  {
    year: "Early years",
    place: "Accra, Ghana",
    body:
      "Raised on Ghanaian storytelling, classroom recitations and the long evenings of Voltarian family gatherings. The conviction that words could move things took root early.",
  },
  {
    year: "Journalism",
    place: "Daily Graphic · Joy News · Press Corps",
    body:
      "A decade reporting on Ghanaian governance and public affairs, including years as a Presidential Correspondent covering executive policy, parliamentary affairs and national elections.",
  },
  {
    year: "Migration",
    place: "Ghana → Canada",
    body:
      "Arriving in Canada with a reporter&rsquo;s notebook and a will to rebuild, Victor retrained while continuing to write &mdash; the cross-Atlantic chapter that shaped his most personal work.",
  },
  {
    year: "Healthcare",
    place: "Ontario, Canada",
    body:
      "Working in frontline healthcare gave him a new vantage on systems, dignity and what it actually costs to keep a country functioning &mdash; a frontline he has not left.",
  },
  {
    year: "Music & Foundation",
    place: "Toronto / Accra",
    body:
      "Music remains his oldest companion. The Delinam Foundation, his civic vehicle, channels his climate-health advocacy back to the communities that raised him.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-cream">
      {/* Editorial header */}
      <section className="container-vk pt-20 md:pt-28 pb-16 md:pb-24">
        <p className="overline mb-6">About Victor</p>
        <h1 className="heading-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight max-w-5xl">
          A Ghanaian&ndash;Canadian life, lived across two newsrooms, two health
          systems, and a single argument about{" "}
          <span className="italic text-burgundy">human dignity</span>.
        </h1>
      </section>

      {/* Lead image + lead column */}
      <section className="container-vk grid lg:grid-cols-12 gap-12 mb-20 md:mb-28">
        <div className="lg:col-span-7">
          <ZoomableImage
            src={IMAGES.aboutPortrait}
            alt="Victor Kwawukume at the Graphic Communications Group newsroom"
            captionOverline="Graphic Communications Group · Accra"
            caption={IMAGE_CAPTIONS.aboutPortrait}
            aspect="aspect-[4/3]"
            testId="about-lead-image"
          />
        </div>
        <div className="lg:col-span-5 space-y-6 text-lg leading-relaxed text-ink/80">
          <p className="first-letter:heading-display first-letter:text-7xl first-letter:font-semibold first-letter:float-left first-letter:mr-3 first-letter:leading-none first-letter:text-burgundy">
            Victor Kwawukume is an award-winning journalist, former Presidential
            Correspondent, healthcare professional, musician, and founder of the
            Delinam Foundation.
          </p>
          <p>
            For more than seventeen years he reported on governance, public
            policy, social development, and national affairs in Ghana, including
            service within the Presidential Press Corps.
          </p>
          <p>
            Today, based in Canada, he combines healthcare service, writing,
            advocacy, and community leadership while building a growing
            catalogue of books exploring resilience, migration, faith, family,
            leadership, and the human condition.
          </p>
          <p>
            His books, journalism, and foundation work are united by one
            conviction: storytelling and service belong together.
          </p>
        </div>
      </section>

      {/* CANADA TRANSITION — Career Journey feature */}
      <section
        data-testid="about-canada-transition"
        className="bg-cream border-y border-paperline py-24 md:py-32"
      >
        <div className="container-vk grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="overline mb-5">Career Journey · Canada</p>
            <h2 className="heading-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.05] text-balance">
              When a celebrated journalism career met{" "}
              <span className="italic text-burgundy">
                the discipline of starting again
              </span>
              .
            </h2>
            <div className="mt-6 space-y-5 text-ink/80 leading-relaxed max-w-xl">
              <p>
                Migration is rarely a single moment &mdash; it is a thousand
                small decisions to keep going. After years inside Ghana&rsquo;s
                executive press corps, Victor arrived in Canada and met every
                opportunity at eye-level: workshop floors, night shifts,
                credentialing exams, and the slow accumulation of new ground
                beneath his feet.
              </p>
              <p>
                The same discipline that sat in cabinet briefings now meets
                power tools and shift schedules &mdash; and the writing has
                never stopped.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <ZoomableImage
              src={IMAGES.canadaTransition}
              alt="Victor Kwawukume working with a mitre saw in a Canadian workshop"
              captionOverline="Canada · Rebuilding"
              caption={IMAGE_CAPTIONS.canadaTransition}
              aspect="aspect-[4/3]"
              testId="canada-transition-image"
            />
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-cream-deep py-24 md:py-32" data-testid="about-timeline">
        <div className="container-vk">
          <p className="overline mb-5">The Crossing, in Chapters</p>
          <h2 className="heading-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.05] max-w-3xl mb-16">
            A life arranged across two continents and several vocations &mdash; held
            together by the discipline of writing it down.
          </h2>
          <ol className="space-y-12 stagger">
            {TIMELINE.map((t, i) => (
              <li
                key={t.year}
                className="grid md:grid-cols-12 gap-6 md:gap-10 animate-fade-up border-t border-paperline pt-8"
              >
                <div className="md:col-span-3">
                  <p className="heading-display text-3xl md:text-4xl font-semibold text-burgundy">
                    0{i + 1}
                  </p>
                  <p className="overline mt-2">{t.year}</p>
                </div>
                <div className="md:col-span-3">
                  <p className="text-ink/70 italic font-serif text-lg">{t.place}</p>
                </div>
                <div
                  className="md:col-span-6 text-ink/80 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: t.body }}
                />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Recognition */}
      <section className="container-vk py-24 md:py-32" data-testid="about-recognition">
        <p className="overline mb-5">Recognition</p>
        <h2 className="heading-display text-3xl sm:text-4xl lg:text-5xl font-semibold mb-12 max-w-3xl">
          Honoured by colleagues. Trusted by sources. Read by the people the
          work was always for.
        </h2>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {AWARDS.map((a) => (
            <li
              key={a}
              className="border border-paperline p-6 text-sm uppercase tracking-[0.14em] text-ink/75 leading-relaxed"
            >
              {a}
            </li>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section className="bg-cream-deep py-20 md:py-28">
        <div className="container-vk grid md:grid-cols-2 gap-8 items-center">
          <h3 className="heading-display text-3xl md:text-4xl font-semibold max-w-xl">
            Interested in a conversation, a column, or a keynote?
          </h3>
          <div className="flex gap-4 md:justify-end">
            <Link to="/speaking" className="btn-secondary" data-testid="about-cta-speaking">
              Speaking
            </Link>
            <Link to="/contact" className="btn-primary" data-testid="about-cta-contact">
              Write to Victor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
